import { useEffect, useState, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MuiFileInput } from 'mui-file-input';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  Checkbox,
  Button,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import OverLayLoader from '../../../components/OverLayLoader';
import FunctionalTable from '../../../components/FunctionalTable';
import NowrapWrapper from '../../../components/NowrapWrapper';
import FileProcessingCard from '../../../components/FileProcessingCard';
import HighlightedInput from '../../../components/HighlightedInput';

import { numberInputOnWheelPreventChange } from '../../../utils/numberInputOnWheelPreventChange';
import {
  getLanguages,
  matchFile,
  uploadFile,
  getFileResult,
  matchText,
} from '../../../services';
import useTitle from '../../../hooks/useTitle';
import useSessionStorage from '../../../hooks/useSessionStorage';
import {
  STATUS_KEYS,
  SOURCE,
  languageOption,
  MATCH_SPECIFY,
  MATCH_METHOD,
  processingFileResultStatus,
} from '../../../helpers/options';
import {
  MATCHING_KEYS,
  CONSTANT_KEYS,
  CLASSIFICATION_KEYS,
} from '../../../helpers/tableKeys';
import statusCodes from '../../../utils/statusCodes';
import isRussian from '../../../utils/isRussian';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import { useAuth } from '../../../context/auth/AuthContext';

const {
  MATCH_TYPE,
  SCORE,
  TASK_ID,
  COLUMN,
  COLUMN_2,
  TASK_TYPE,
  METHOD,
  GUARANTEE_LETTERS_TEXT,
  CODE_NUMBERS,
  GUARANTEE_LETTERS_APPROVE_SCORE,
  MATCH_SCORE,
  ELEM,
  CROSS_ELEM,
  CODE,
  CROSS_CODE,
} = MATCHING_KEYS;
const { LANGUAGE_NAME, PROFILE_NAME, SERVICES, NAME, ID } = CONSTANT_KEYS;
const { CLEANED_SERVICE_NAME } = CLASSIFICATION_KEYS;

const MatchingView = () => {
  const [data, setData] = useState(() => ({
    language: '',
    profile: '',
    score: '0.6',
    source: SOURCE.TEXT,
    specify: MATCH_SPECIFY.GUARANTEE,
    method: Object.keys(MATCH_METHOD).map(key => MATCH_METHOD[key]),
    text1: '',
    text2: '',
  }));
  const [file, setFile] = useState(null);
  const [fileResultUrl, setFileResultUrl] = useState('');
  const [fileResultTaskId, setFileResultTaskId] = useState('');
  const [fileName, setFileName] = useState('');
  const [isFileLoading, setIsFileLoading] = useState(false);

  const [columnsData, setColumnsData] = useState(null);
  const [languageList, setLanguageList] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [tableData, setTableData] = useState([]);

  const [status, setStatus] = useState(STATUS_KEYS.IDLE);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);

  const intervalRef = useRef(null);
  const fileInputRef = useRef(null);

  const { user } = useAuth();

  const { t } = useTranslation(['matching', 'common', 'promptbar']);

  const title = t(`matching:matching`);
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  const [sessionData, setSessionData] = useSessionStorage('matchingColumns', {
    profiles: '',
  });

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchLanguages = async () => {
      try {
        const langs = await getLanguages();
        setLanguageList(langs.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsOptionsLoading(false);
      }
    };

    setProfiles(user.profiles);
    fetchLanguages();
  }, [user]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Вычисляем язык
  const resolvedLanguage = useMemo(() => {
    if (!languageList.length || !user) return '';

    return (
      languageList.find(el => el.id === user.data_language_id)?.name ||
      languageOption[0]
    );
  }, [languageList, user]);

  // Вычисляем итоговый профиль
  const resolvedProfile = useMemo(() => {
    if (!user?.profiles?.length) return '';

    // Находим default профиль пользователя
    const firstDefaultProfile = user.profiles.find(
      pr => pr.is_default,
    )?.profile_name;

    const defaultProfile =
      firstDefaultProfile || user.profiles[0]?.profile_name;

    // Проверяем валидность сохранённого профиля
    const isSessionProfileValid = user.profiles.some(
      p => p.profile_name === sessionData.profiles,
    );
    // Приоритет:
    // sessionStorage → если валиден
    // иначе → defaultProfile
    return isSessionProfileValid ? sessionData.profiles : defaultProfile;
  }, [user, sessionData.profiles]);

  useEffect(() => {
    if (!resolvedProfile || !resolvedLanguage) return;

    setData(prev => ({
      ...prev,
      profile: resolvedProfile,
      language: resolvedLanguage,
    }));
  }, [resolvedProfile, resolvedLanguage]);

  const handleSubmit = async () => {
    setStatus(STATUS_KEYS.PENDING);
    setFileResultTaskId('');
    setFileResultUrl('');
    setFileName('');
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    try {
      if (data.source === SOURCE.TEXT) {
        const newData = {
          [SERVICES]: [
            {
              [ID]: '',
              [NAME]: data.text1,
              [GUARANTEE_LETTERS_TEXT]: data.text2,
              [CODE_NUMBERS]: '',
              [CLEANED_SERVICE_NAME]: '',
            },
          ],
          [GUARANTEE_LETTERS_APPROVE_SCORE]: data.score,
          [LANGUAGE_NAME]: data.language,
          [PROFILE_NAME]: data.profile,
          [TASK_TYPE]: data.specify,
          [METHOD]: data.method,
        };

        const result = await matchText(newData);

        if (result?.data) {
          setTableData(result.data);
          setStatus(STATUS_KEYS.RESOLVED);
        }
      }

      if (data.source === SOURCE.FILE) {
        if (!columnsData?.[TASK_ID]) {
          return;
        }
        const newData = {
          [LANGUAGE_NAME]: data.language,
          [PROFILE_NAME]: data.profile,
          [TASK_ID]: columnsData[TASK_ID],
          [SCORE]: data.score,
          [COLUMN]: data.text1,
          [COLUMN_2]: data.text2,
          [TASK_TYPE]: data.specify,
          [METHOD]: data.method,
        };
        const result = await matchFile(newData);

        if (result) {
          const taskId = result.data.task_id;
          setFileResultTaskId(taskId);

          intervalRef.current = setInterval(async () => {
            try {
              const response = await getFileResult(taskId);

              if (
                (response?.data?.status ===
                  processingFileResultStatus.SUCCESS &&
                  response?.data?.file_url) ||
                response?.data?.status === processingFileResultStatus.FAILURE
              ) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
                setFileName(response?.data?.orig_filename || '');
                setFileResultUrl(response?.data?.file_url);
                setStatus(
                  response.data.status === processingFileResultStatus.FAILURE
                    ? STATUS_KEYS.REJECTED
                    : STATUS_KEYS.RESOLVED,
                );
              }
            } catch (error) {
              console.error('Ошибка при получении результата', error);
              clearInterval(intervalRef.current);
              intervalRef.current = null;
              createErrorNotification();
              setStatus(STATUS_KEYS.REJECTED);
            }
          }, 2000);
        }
      }
    } catch (error) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
      createErrorNotification();
      setStatus(STATUS_KEYS.REJECTED);
      console.log(error);
    }
  };

  const handleInputChange = event => {
    const { value, name } = event.target;

    switch (name) {
      case 'source':
        if (value === 'text') {
          setData(prev => ({ ...prev, [name]: value, text1: '', text2: '' }));
          setFile(null);
          setFileResultUrl('');
          setFileResultTaskId('');
          setFileName('');
          setColumnsData(null);
          clearInterval(intervalRef.current);
          setStatus(STATUS_KEYS.IDLE);
        }
        if (value === 'file') {
          setData(prev => ({ ...prev, [name]: value, text1: '', text2: '' }));
          setStatus(STATUS_KEYS.IDLE);
        }
        break;

      case 'text1': {
        if (data.source === SOURCE.FILE && value === data.text2) {
          return;
        } else {
          setData(prev => ({ ...prev, [name]: value }));
        }
        break;
      }

      case 'text2': {
        if (data.source === SOURCE.FILE && value === data.text1) {
          return;
        } else {
          setData(prev => ({ ...prev, [name]: value }));
        }
        break;
      }

      case 'method': {
        if (value?.length === 0) {
          return;
        } else {
          setData(prev => ({ ...prev, [name]: value }));
        }

        break;
      }

      default:
        setData(prev => ({ ...prev, [name]: value }));

        // Если изменился профиль — сохраняем его в sessionStorage
        if (name === 'profile') {
          setSessionData(prev => ({
            ...prev,
            profiles: value,
          }));
        }
        break;
    }
  };

  const handleFileChange = async newFile => {
    try {
      setFile(null);
      setColumnsData(null);
      setFileResultUrl('');
      setFileResultTaskId('');
      setFileName('');
      setStatus(STATUS_KEYS.IDLE);
      clearInterval(intervalRef.current);
      setData(prev => ({ ...prev, text1: '', text2: '' }));

      if (!newFile) {
        return;
      }

      setTimeout(() => setFile(newFile), 0);

      setIsFileLoading(true);

      const response = await uploadFile({ file: newFile });
      if (response) {
        if (response.data?.columns?.length < 2) {
          createErrorNotification(
            t('No data for matching was found in the uploaded file'),
          );
        } else {
          setColumnsData(response.data);
        }
      }
      setIsFileLoading(false);
    } catch (error) {
      setFile(null);
      setIsFileLoading(false);
      if (
        error?.response?.status === statusCodes.HTTP_422_UNPROCESSABLE_ENTITY
      ) {
        const message = error?.response?.data?.detail || '';
        const isUserMessage = isRussian(message);
        if (isUserMessage) {
          return createErrorNotification(message);
        } else {
          const isFormatError = /[no key such]/i.test(message);
          if (isFormatError) {
            return createErrorNotification(
              t(
                'File reading error. Please ensure the uploaded file is in xlsx format',
              ),
            );
          }
          return createErrorNotification();
        }
      }
      createErrorNotification();
    } finally {
      fileInputRef.current.value = null;
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t('elem1')} />,
        accessor: ELEM,
        className: 'center',
        style: { width: '28%' },
      },
      {
        Header: <NowrapWrapper title={t('code1')} />,
        accessor: CODE,
        className: 'center',
        style: { width: '11%' },
      },
      {
        Header: <NowrapWrapper title={t('elem2')} />,
        accessor: CROSS_ELEM,
        className: 'center',
        style: { width: '28%' },
      },
      {
        Header: <NowrapWrapper title={t('code2')} />,
        accessor: CROSS_CODE,
        className: 'center',
        style: { width: '11%' },
      },
      {
        Header: <NowrapWrapper title={t('score')} />,
        accessor: MATCH_SCORE,
        className: 'center',
        style: { width: '11%' },
        Cell: ({ value }) => parseFloat(value.toFixed(2)),
      },
      {
        Header: <NowrapWrapper title={t('matchType')} />,
        accessor: MATCH_TYPE,
        className: 'center',
        style: { width: '11%' },
      },
    ],
    [t],
  );

  const isMatchBtnDisabled =
    (data.source === SOURCE.FILE && (!data.text1 || !data.text2 || !file)) ||
    status === STATUS_KEYS.PENDING ||
    isFileLoading;

  return (
    <Paper elevation={3} sx={{ padding: '2.5rem 2rem 2rem' }}>
      {(isFileLoading || isOptionsLoading) && <OverLayLoader />}
      <Box
        component="form"
        autoComplete="off"
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '2.5rem',
          }}
        >
          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-language-label" shrink={false}>
              {t('common:dictionaryLanguage')}
            </InputLabel>
            <Select
              labelId="Select-language-label"
              id="Select-language"
              name="language"
              value={data.language}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:dictionaryLanguage')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              {languageList.map(lng => (
                <MenuItem key={lng.id} value={lng.name}>
                  <ListItemText primary={lng.name.toUpperCase()} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-profile-label" shrink={false}>
              {t('common:profile')}
            </InputLabel>
            <Select
              labelId="Select-profile-label"
              id="Select-profile"
              name="profile"
              value={data.profile}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:profile')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              {profiles.map(el => (
                <MenuItem key={el.id} value={el.profile_name}>
                  <ListItemText primary={el.profile_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Input-score-label" shrink={false}>
              {t('Threshold score')}
            </InputLabel>
            <OutlinedInput
              disabled
              id="Input-score"
              name="score"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.01 }}
              value={data.score}
              onChange={handleInputChange}
              onWheel={numberInputOnWheelPreventChange}
            />
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-source-label" shrink={false}>
              {t('common:source')}
            </InputLabel>
            <Select
              labelId="Select-source-label"
              id="Select-source"
              name="source"
              value={data.source}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('common:source')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              <MenuItem key={SOURCE.TEXT} value={SOURCE.TEXT}>
                <ListItemText primary={t('common:text')} />
              </MenuItem>
              <MenuItem key={SOURCE.FILE} value={SOURCE.FILE}>
                <ListItemText primary={t('common:file')} />
              </MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-specify-label" shrink={false}>
              {t('matching:specify')}
            </InputLabel>
            <Select
              labelId="Select-specify-label"
              id="Select-specify"
              name="specify"
              value={data.specify}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('matching:specify')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
            >
              {Object.keys(MATCH_SPECIFY).map(key => (
                <MenuItem key={MATCH_SPECIFY[key]} value={MATCH_SPECIFY[key]}>
                  <ListItemText primary={t(`${MATCH_SPECIFY[key]}`)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="Select-method-label" shrink={false}>
              {t('matching:method')}
            </InputLabel>
            <Select
              labelId="Select-method-label"
              id="Select-method"
              name="method"
              multiple
              value={data.method}
              onChange={handleInputChange}
              input={<OutlinedInput label={t('matching:method')} />}
              IconComponent={KeyboardArrowDownIcon}
              MenuProps={{ size: 'small', autoFocus: false }}
              renderValue={selected =>
                selected.map(el => t(`${el}`)).join('; ')
              }
            >
              {Object.keys(MATCH_METHOD).map(key => (
                <MenuItem key={MATCH_METHOD[key]} value={MATCH_METHOD[key]}>
                  <Checkbox
                    checked={data.method.indexOf(MATCH_METHOD[key]) > -1}
                  />
                  <ListItemText primary={t(`${MATCH_METHOD[key]}`)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box
          sx={{
            display: data.source === SOURCE.TEXT ? 'grid' : 'flex',
            gridTemplateColumns:
              data.source === SOURCE.TEXT ? 'repeat(2, 1fr)' : 'none',
            alignItems: data.source === SOURCE.TEXT ? 'stretch' : 'initial',
            gap: '2.5rem',
            mt: '3rem',
          }}
        >
          {data.source === SOURCE.TEXT && (
            <>
              <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                <InputLabel id="Input-text1-label" shrink={false}>
                  {t(`matching:text1.${data.specify}`)}
                </InputLabel>

                <HighlightedInput
                  inputProps={{ id: 'Input-text1', name: 'text1' }}
                  value={data.text1}
                  onChange={handleInputChange}
                  lang={data.language}
                  minRows={1}
                  spaceMarkerMode="none"
                />
              </FormControl>

              <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                <InputLabel id="Input-text2-label" shrink={false}>
                  {t(`matching:text2.${data.specify}`)}
                </InputLabel>
                <HighlightedInput
                  inputProps={{ id: 'Input-text2', name: 'text2' }}
                  value={data.text2}
                  onChange={handleInputChange}
                  lang={data.language}
                  minRows={1}
                  spaceMarkerMode="none"
                />
              </FormControl>
            </>
          )}
          {data.source === SOURCE.FILE && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2.5rem',
                width: '100%',
              }}
            >
              <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                <InputLabel id="Input-file-label" shrink={false}>
                  {t('common:file')}
                </InputLabel>
                <MuiFileInput
                  inputRef={fileInputRef}
                  value={file}
                  onChange={handleFileChange}
                  InputProps={{
                    inputProps: {
                      accept: '.xlsx',
                    },
                    startAdornment: <AttachFileIcon />,
                  }}
                />
              </FormControl>

              {file && columnsData && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '2.5rem',
                  }}
                >
                  <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                    <InputLabel id="Select-text1-label" shrink={false}>
                      {t(`matching:text1.${data.specify}`)}
                    </InputLabel>
                    <Select
                      labelId="Select-text1-label"
                      id="Select-text1"
                      name="text1"
                      value={data.text1}
                      onChange={handleInputChange}
                      input={<OutlinedInput label={t('common:column_name')} />}
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{ size: 'small', autoFocus: false }}
                    >
                      {columnsData?.columns?.map(el => (
                        <MenuItem key={el} value={el}>
                          <ListItemText primary={el} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
                    <InputLabel id="Select-text2-label" shrink={false}>
                      {t(`matching:text2.${data.specify}`)}
                    </InputLabel>
                    <Select
                      labelId="Select-text2-label"
                      id="Select-text2"
                      name="text2"
                      value={data.text2}
                      onChange={handleInputChange}
                      input={<OutlinedInput label={t('common:column_name')} />}
                      IconComponent={KeyboardArrowDownIcon}
                      MenuProps={{ size: 'small', autoFocus: false }}
                    >
                      {columnsData?.columns?.map(el => (
                        <MenuItem key={el} value={el}>
                          <ListItemText primary={el} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          sx={{ display: 'flex', mx: 'auto', mt: 5 }}
          type="button"
          disabled={isMatchBtnDisabled}
          onClick={handleSubmit}
        >
          {t('promptbar:match')}
        </Button>
      </Box>

      {/* результат мэтчинга */}
      {status !== STATUS_KEYS.IDLE && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            mt: '2rem',
            gap: '0.5rem',
          }}
        >
          <FormControl fullWidth variant="standard" sx={{ m: 0 }}>
            <InputLabel id="match-result-label" shrink={false}>
              {t('matching:match')}
            </InputLabel>
          </FormControl>

          {data.source === SOURCE.TEXT && (
            <>
              {status === STATUS_KEYS.RESOLVED && (
                <FunctionalTable
                  data={tableData}
                  columns={columns}
                  isActionColumn={false}
                  isFilter={false}
                />
              )}
              {status === STATUS_KEYS.PENDING && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} thickness={3} color="secondary" />
                  <Typography component="p" sx={{ fontWeight: 'bold' }}>
                    {t('The matching is in progress') + '...'}
                  </Typography>
                </Box>
              )}
              {status === STATUS_KEYS.REJECTED && (
                <Typography component="p" sx={{ fontWeight: 'bold' }} mb={3}>
                  {t('An error occurred during the matching process') + '.'}
                </Typography>
              )}
            </>
          )}
          {data.source === SOURCE.FILE && (
            <FileProcessingCard
              pendingText={
                t('The matching of the file is in progress', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '...'
              }
              resolvedText={
                t('The matching of the file is complete', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '!'
              }
              errorText={
                t('An error occurred during the matching of the file', {
                  fileName: fileName ? ` "${fileName}"` : '',
                }) + '.'
              }
              fileId={fileResultTaskId}
              status={status}
              fileResultUrl={fileResultUrl}
            />
          )}
        </Box>
      )}
    </Paper>
  );
};

export default MatchingView;
